import { useCallback, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import api from "utils/api";
import { ANALYTICS_EVENTS, trackEvent } from "utils/analytics";
import type {
  UsePageScaleRecalibrationArgs,
  UsePageScaleRecalibrationResult,
} from "./types";

// Single-page review UI: page 0 is the active page.
const ACTIVE_PAGE_INDEX = 0;

/** Standalone re-calibration: page-scale provenance/gates, confirm dialog, reset-to-AI. */
export const usePageScaleRecalibration = ({
  selectedPlan,
  interactionMode,
  startRecalibrating,
  onSuccess,
}: UsePageScaleRecalibrationArgs): UsePageScaleRecalibrationResult => {
  const { t } = useTranslation();
  const [isConfirmOpen, setIsConfirmOpen] = useState(false);
  const [isResetting, setIsResetting] = useState(false);

  const pageScale =
    selectedPlan?.metersPerPdfPointByPageIndex.get(ACTIVE_PAGE_INDEX) ?? 0;

  const pageScaleSource =
    selectedPlan?.metersPerPdfPointSourceByPageIndex.get(ACTIVE_PAGE_INDEX) ??
    null;

  const detectedScale =
    selectedPlan?.detectedMetersPerPdfPointByPageIndex.get(ACTIVE_PAGE_INDEX) ??
    null;

  const isPageCalibrated = pageScale > 0;
  const isPageRecalibrating =
    interactionMode.kind === "calibrating" &&
    interactionMode.origin === "standalone";

  // Only startable from idle, so it can't begin mid draw/edit.
  const isChangePageScaleDisabled = interactionMode.kind !== "idle";

  // Offer reset only when the AI value exists and the user has moved away from it.
  const canResetToDetected =
    !isChangePageScaleDisabled &&
    !isResetting &&
    detectedScale !== null &&
    detectedScale !== pageScale;

  const openConfirm = useCallback(() => {
    setIsConfirmOpen(true);
  }, []);

  const closeConfirm = useCallback(() => {
    setIsConfirmOpen(false);
  }, []);

  const confirmRecalibrate = useCallback(() => {
    startRecalibrating({ pageIndex: ACTIVE_PAGE_INDEX });
  }, [startRecalibrating]);

  const resetToDetected = useCallback(async () => {
    if (!selectedPlan || detectedScale === null) return;

    const pageId = selectedPlan.pageIdByIndex.get(ACTIVE_PAGE_INDEX);
    if (pageId === undefined) return;

    setIsResetting(true);
    try {
      // Re-submitting the detected value makes the backend stamp
      // source=automatic, restoring the "AI Detected" provenance.
      await api.updateQuantitySurveyorPageCalibration({
        pageId,
        metersPerPdfPoint: detectedScale,
      });
      trackEvent(ANALYTICS_EVENTS.SMART_SURVEYOR_PAGE_SCALE_SET, {
        method: "resetToDetected",
      });
      onSuccess({
        floorPlanId: selectedPlan.floorPlanId,
        pageIndex: ACTIVE_PAGE_INDEX,
        metersPerPdfPoint: detectedScale,
        source: "automatic",
      });
      toast.success(t("smartSurveyor.pageCalibration.scaleReset"));
    } catch (error) {
      toast.error(
        error instanceof Error
          ? error.message
          : t("smartSurveyor.pageCalibration.failedToSave"),
      );
    } finally {
      setIsResetting(false);
    }
  }, [selectedPlan, detectedScale, onSuccess, t]);

  return {
    pageScale,
    pageScaleSource,
    isPageCalibrated,
    isPageRecalibrating,
    isChangePageScaleDisabled,
    isConfirmOpen,
    openConfirm,
    closeConfirm,
    confirmRecalibrate,
    canResetToDetected,
    resetToDetected,
  };
};
